import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

type UseRouterLoadingOptions = {
  /**
   * Debounce delay in milliseconds before clearing loading state
   * @default 500
   */
  debounceDelay?: number;
  /**
   * Whether to prevent loading state when the same path is navigated to
   * @default false
   */
  noLoadingOnSamePath?: boolean;
};

type UseRouterLoadingReturn = {
  isLoading: boolean;
};

/**
 * Simple router loading hook that shows loading state during route changes
 *
 * - Sets loading to true immediately when route change starts
 * - Clears loading state with debounce when route change completes
 */
export function useRouterLoading(options: UseRouterLoadingOptions = {}): UseRouterLoadingReturn {
  const { debounceDelay = 500, noLoadingOnSamePath = false } = options;
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAsPathRef = useRef<string | null>(null);

  useEffect(() => {
    lastAsPathRef.current = router.pathname;
    
    const handleRouteChangeStart = (asPath: string) => {
      const pathName = asPath.split('?')[0];
      const lastPathName = lastAsPathRef.current?.split('?')[0];
      if (noLoadingOnSamePath && lastPathName === pathName) return;

      lastAsPathRef.current = asPath;

      setIsLoading(true);
    };

    const handleRouteChangeFinished = () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Debounce clearing the loading state
      debounceTimeoutRef.current = setTimeout(() => {
        setIsLoading(false);
      }, debounceDelay);
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeFinished);
    router.events.on('routeChangeError', handleRouteChangeFinished);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        setIsLoading(false);
      }
      lastAsPathRef.current = null;
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeFinished);
      router.events.off('routeChangeError', handleRouteChangeFinished);
    };
  }, [router, debounceDelay]);

  return { isLoading };
}

export default useRouterLoading;
